'use client'

import Script from 'next/script'
import { useEffect } from 'react'

/**
 * Loads reCAPTCHA v3 only when the contact form is opened, so we don't trigger
 * Google third-party cookies/requests on every page load. Reduces third-party
 * cookie surface and prepares for cookie restrictions.
 *
 * Do not remove the badge node or its iframes — that breaks verification
 * (browser-error). We only apply inline styles; Google often overrides
 * globals.css with inline rules, so we re-apply when the DOM changes.
 */
function hideRecaptchaBadge() {
  document.querySelectorAll('.grecaptcha-badge').forEach((node) => {
    const el = node as HTMLElement
    el.style.setProperty('visibility', 'hidden', 'important')
    el.style.setProperty('opacity', '0', 'important')
    el.style.setProperty('pointer-events', 'none', 'important')
    el.style.setProperty('position', 'fixed', 'important')
    el.style.setProperty('right', '-100vw', 'important')
    el.style.setProperty('bottom', '-100vh', 'important')
    el.style.setProperty('width', '0', 'important')
    el.style.setProperty('height', '0', 'important')
    el.style.setProperty('min-width', '0', 'important')
    el.style.setProperty('min-height', '0', 'important')
    el.style.setProperty('overflow', 'hidden', 'important')
    el.style.setProperty('z-index', '-1', 'important')
  })
}

export default function RecaptchaLoader() {
  const siteKey = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY

  useEffect(() => {
    if (!siteKey) return
    hideRecaptchaBadge()
    const observer = new MutationObserver(() => {
      hideRecaptchaBadge()
    })
    observer.observe(document.body, { childList: true, subtree: true })
    const intervalId = window.setInterval(hideRecaptchaBadge, 2000)
    return () => {
      observer.disconnect()
      window.clearInterval(intervalId)
    }
  }, [siteKey])

  if (!siteKey) return null

  return (
    <Script
      src={`https://www.google.com/recaptcha/api.js?render=${siteKey}`}
      strategy="afterInteractive"
    />
  )
}
